import { useMemo, useState } from "react";

import { bootstrapTeam, friendlyError } from "../../data/callables";
import {
  buildMigrationSeed,
  hasLocalData,
  isMigrated,
  markMigrated,
  readLocalData,
} from "../../data/migration";
import { saveTeamId } from "../../data/teamSession";
import { Button } from "../../shared/ui/button";
import { Field, FieldError } from "../../shared/ui/field";
import { Input } from "../../shared/ui/input";
import { Label } from "../../shared/ui/label";
import {
  OnboardingContent,
  OnboardingFrame,
  OnboardingHeading,
} from "./OnboardingFrame";

export function migrationAvailable(): boolean {
  return hasLocalData() && !isMigrated();
}

function countLegacyPlayers(current: unknown): number {
  if (
    typeof current !== "object" ||
    current === null ||
    !("players" in current)
  ) {
    return 0;
  }
  const players = current.players;
  return Array.isArray(players)
    ? players.filter(
        (player) =>
          typeof player === "object" &&
          player !== null &&
          "name" in player &&
          typeof player.name === "string" &&
          Boolean(player.name.trim()),
      ).length
    : 0;
}

export function Migrate({ onCancel }: { onCancel: () => void }) {
  const [{ current, games }] = useState(() => readLocalData());
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  const playerCount = useMemo(() => countLegacyPlayers(current), [current]);

  async function doImport(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const { teamName, seed } = buildMigrationSeed({ current, games });
      const result = await bootstrapTeam({ secret, teamName, seed });
      markMigrated();
      saveTeamId(result.data.teamId);
      setInviteUrl(
        `${window.location.origin}/join/${result.data.teamId}#token=${result.data.inviteToken}`,
      );
    } catch (importError) {
      setError(friendlyError(importError));
    } finally {
      setBusy(false);
    }
  }

  const archiveSummary = games.length
    ? ` and ${games.length} archived game${games.length === 1 ? "" : "s"}`
    : "";

  return (
    <OnboardingFrame eyebrow="One-time local data migration">
      <OnboardingHeading
        title="IMPORT TEAM DATA"
        description={
          inviteUrl
            ? `Import complete — ${playerCount} players${archiveSummary} moved to your new team.`
            : `Found ${playerCount} players${archiveSummary} on this browser. Import only from the browser holding the authoritative local data.`
        }
      />
      <OnboardingContent>
        {inviteUrl ? (
          <>
            <p className="m-0 text-sm leading-relaxed text-foreground-muted">
              Share this fixed invite link with teammates. You can copy it later
              under Manage → Invite teammates.
            </p>
            <Field>
              <Label htmlFor="migration-invite">Team invite link</Label>
              <Input
                id="migration-invite"
                readOnly
                value={inviteUrl}
                onFocus={(event) => event.target.select()}
                className="font-mono text-xs"
              />
            </Field>
            <Button
              type="button"
              onClick={() => window.location.reload()}
              className="w-full"
            >
              CONTINUE TO YOUR TEAM
            </Button>
          </>
        ) : (
          <>
            <Field>
              <Label htmlFor="bootstrap-secret">Bootstrap secret</Label>
              <Input
                id="bootstrap-secret"
                type="password"
                placeholder="Bootstrap secret"
                value={secret}
                disabled={busy}
                onChange={(event) => setSecret(event.target.value)}
              />
            </Field>
            {error ? (
              <FieldError className="text-center">{error}</FieldError>
            ) : null}
            <Button
              type="button"
              disabled={!secret}
              pending={busy}
              pendingLabel="IMPORTING…"
              onClick={doImport}
              className="w-full"
            >
              IMPORT & CREATE TEAM
            </Button>
            <Button
              type="button"
              onClick={onCancel}
              disabled={busy}
              variant="ghost"
              size="sm"
              className="w-full"
            >
              Cancel
            </Button>
          </>
        )}
      </OnboardingContent>
    </OnboardingFrame>
  );
}
